import { createContext, useContext, type ReactNode } from 'react';
import type { IntrospectableSchema } from './introspect';

export type CrudOp = 'create' | 'read' | 'update' | 'delete';

/** Class-name slots for theming the components. All optional; sensible
 *  framework-neutral defaults apply when omitted. */
export type ResourceUITheme = {
  /** Shared default for `<input>`/`<select>` across components. */
  input?: string;
  table?: {
    root?: string;
    /** The scroll box wrapping the <table> — theme it into a card if desired. */
    container?: string;
    table?: string;
    thead?: string;
    th?: string;
    td?: string;
    row?: string;
    rowSelected?: string;
    pager?: string;
    pageBtn?: string;
  };
  filters?: {
    root?: string;
    field?: string;
    label?: string;
    clear?: string;
  };
  form?: {
    root?: string;
    label?: string;
    submit?: string;
    cancel?: string;
    error?: string;
  };
};

/** UI strings — pass your i18n here once. */
export type ResourceUITexts = {
  table?: { error?: string; loading?: string; empty?: string; page?: string; prev?: string; next?: string };
  filters?: { all?: string; clear?: string; yes?: string; no?: string; min?: string; max?: string; from?: string; to?: string };
  form?: {
    create?: string;
    save?: string;
    cancel?: string;
    saving?: string;
    invalidJson?: string;
    required?: string;
    // Model-interpolated.
    createHeader?: (model: string) => string;
    editHeader?: (model: string) => string;
    created?: (model: string) => string;
    updated?: (model: string) => string;
  };
};

/** Create + update mutations for one model — typically your ZenStack
 *  `useCreate()` / `useUpdate()` wired by a hook. */
export type ResourceMutations = {
  create: {
    mutateAsync: (args: { data: Record<string, unknown> }) => Promise<unknown>;
    isPending: boolean;
  };
  update: {
    mutateAsync: (args: {
      where: Record<string, unknown>;
      data: Record<string, unknown>;
    }) => Promise<unknown>;
    isPending: boolean;
  };
};

export type ResourceUIConfig = {
  /** The ZenStack schema object (e.g. schema-lite). Drives all introspection. */
  schema: IntrospectableSchema;
  /** (model, field) → header/label. Default: title-cased field name. */
  label?: (model: string, field: string) => string;
  /** (model, field, value) → enum option label. Default: the raw value. */
  enumLabel?: (model: string, field: string, value: string) => string;
  texts?: ResourceUITexts;
  theme?: ResourceUITheme;
  /** Policy hook — bind it to your ZenStack `@@allow` evaluator. Used for
   *  action visibility in the table (and field gating in the form). */
  can?: (model: string, op: CrudOp, auth: unknown, row?: unknown) => boolean;
  /** Field-level policy — gates which fields the form shows in edit mode. */
  canField?: (model: string, field: string, op: CrudOp, auth: unknown, row?: unknown) => boolean;
  /** Create/update mutations for a model — a hook, called inside ResourceForm.
   *  Wire it to your ZenStack client (e.g. useCreate/useUpdate). Required to
   *  use <ResourceForm>. */
  useMutation?: (model: string) => ResourceMutations;
  /** Map a thrown error to a user message (e.g. your CodedError translator). */
  formatError?: (err: unknown) => string;
  /** Success notification (e.g. a toast). */
  notify?: (message: string) => void;
};

const Ctx = createContext<ResourceUIConfig | null>(null);

/**
 * Configure the kit once near your app root: pass your generated `schema`, plus
 * optional theme / i18n texts / labels / policy. Every `@diadraw/ui` component
 * reads from here.
 */
export function ResourceUIProvider({
  children,
  ...config
}: ResourceUIConfig & { children: ReactNode }) {
  return <Ctx.Provider value={config}>{children}</Ctx.Provider>;
}

export function useResourceUI(): ResourceUIConfig {
  const c = useContext(Ctx);
  if (!c) {
    throw new Error('Wrap your app in <ResourceUIProvider> to use @diadraw/ui components.');
  }
  return c;
}

/** Default title-caser used when no `label` is configured. */
export const titleCase = (s: string) =>
  s
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
